// Global Instructions Rule Applied!
// Shapes of the Commitment forms' reads (function_view__commitment_forms.sql):
//   GET /funds/commitments/:id              — view__fund_commitment_detail
//   GET /fund-groups/subscriptions/:id      — view__fund_group_commitment_detail
//   GET /funds/commitment-form-options      — view__commitment_form_options
// Rates are fractions (0.02 = 2%); dates 'YYYY-MM-DD'. Ids are plain
// z.string() (legacy Dataverse ids preclude .uuid()).
import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

// A standalone commitment, or a pocket of a consolidated commitment (then
// fund_commitments__fund_group_commitment_id and fund_groups__* are set).
export const FundCommitmentDetailSchema = z
  .object({
    fund_commitments__id: z.string(),
    fund_commitments__amount: z.number().nullable(),
    fund_commitment_statuses__id: z.number().nullable(),
    fund_commitment_statuses__label: z.string().nullable(),
    fund_commitments__date_committed: z.string().nullable(),
    fund_commitments__date_closed: z.string().nullable(),
    fund_commitments__management_fee_rate: z.number().nullable(),
    fund_commitments__base_carry_rate: z.number().nullable(),
    fund_commitments__base_hurdle_multiple: z.number().nullable(),
    fund_commitments__stepped_carry_rate: z.number().nullable(),
    fund_commitments__stepped_hurdle_multiple: z.number().nullable(),
    fund_commitments__is_gp_commitment: z.boolean(),
    // the row the fund's carried interest is allocated and paid to
    fund_commitments__is_carry_recipient: z.boolean(),
    // true = the commitment carries its own notice documents, not its investor's
    fund_commitments__notice_supplements_overridden: z.boolean(),
    prog__notice_supplement_template_ids: z.array(z.number()),
    prog__investor_notice_supplement_template_ids: z.array(z.number()),
    funds__id: z.string(),
    funds__name: z.string().nullable(),
    currencies__symbol: z.string().nullable(),
    funds__mgmt_fee_rate: z.number().nullable(),
    // the fund's default carry terms: the Terms section's placeholders
    funds__base_carry_rate: z.number().nullish(),
    funds__base_hurdle_multiple: z.number().nullish(),
    funds__stepped_carry_rate: z.number().nullish(),
    funds__stepped_hurdle_multiple: z.number().nullish(),
    funds__is_sbic: z.boolean(),
    investors__id: z.string().nullable(),
    investors__name: z.string().nullable(),
    accounts__id: z.string().nullable(),
    accounts__name: z.string().nullable(),
    fund_commitments__fund_group_commitment_id: z.string().nullable(),
    fund_groups__id: z.string().nullable(),
    fund_groups__name: z.string().nullable()
  })
  .strict()

export const FundCommitmentDetailFields = makeFieldKeys(FundCommitmentDetailSchema)

// One member fund of the consolidated fund, allocated or not.
export const FundGroupCommitmentAllocationSchema = z
  .object({
    fund_group_members__id: z.string(),
    funds__id: z.string(),
    funds__name: z.string().nullable(),
    fund_group_members__is_primary: z.boolean(),
    fund_group_members__default_allocation_pct: z.number().nullable(),
    // null = nothing allocated to this member
    fund_group_commitment_allocations__amount: z.number().nullable(),
    // satellite only, raw: null = as the consolidated commitment
    fund_group_commitment_allocations__status_id: z.number().nullable(),
    fund_group_commitment_allocations__date_closed: z.string().nullable(),
    // the member fund's pocket, when there is one
    fund_commitments__id: z.string().nullable()
  })
  .strict()

export const FundGroupCommitmentDetailSchema = z
  .object({
    fund_group_commitments__id: z.string(),
    fund_group_commitments__amount: z.number().nullable(),
    fund_commitment_statuses__id: z.number().nullable(),
    fund_commitment_statuses__label: z.string().nullable(),
    fund_group_commitments__date_committed: z.string().nullable(),
    fund_group_commitments__date_closed: z.string().nullable(),
    fund_group_commitments__is_gp_commitment: z.boolean(),
    fund_group_commitments__is_carry_recipient: z.boolean(),
    fund_groups__id: z.string(),
    fund_groups__name: z.string().nullable(),
    currencies__symbol: z.string().nullable(),
    investors__id: z.string().nullable(),
    investors__name: z.string().nullable(),
    accounts__id: z.string().nullable(),
    accounts__name: z.string().nullable(),
    // the notice setting, as held by the primary pocket
    prog__notice_supplements_overridden: z.boolean(),
    prog__notice_supplement_template_ids: z.array(z.number()),
    prog__investor_notice_supplement_template_ids: z.array(z.number()),
    prog__allocations: z.array(FundGroupCommitmentAllocationSchema)
  })
  .strict()

export const FundGroupCommitmentDetailFields = makeFieldKeys(FundGroupCommitmentDetailSchema)

const NumberOptionSchema = z.object({ value: z.number(), label: z.string() }).strict()

export const CommitmentInvestorOptionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    // the documents the investor's notices carry by default
    prog__notice_supplement_template_ids: z.array(z.number())
  })
  .strict()

export const CommitmentFundOptionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    currencies__symbol: z.string().nullable(),
    funds__mgmt_fee_rate: z.number().nullable(),
    funds__base_carry_rate: z.number().nullish(),
    funds__base_hurdle_multiple: z.number().nullish(),
    funds__stepped_carry_rate: z.number().nullish(),
    funds__stepped_hurdle_multiple: z.number().nullish(),
    funds__is_sbic: z.boolean()
  })
  .strict()

export const CommitmentFundGroupMemberSchema = z
  .object({
    fund_group_members__id: z.string(),
    funds__id: z.string(),
    funds__name: z.string().nullable(),
    fund_group_members__is_primary: z.boolean(),
    fund_group_members__default_allocation_pct: z.number().nullable()
  })
  .strict()

export const CommitmentFundGroupOptionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    currencies__symbol: z.string().nullable(),
    // any member fund is an SBIC fund
    funds__is_sbic: z.boolean(),
    members: z.array(CommitmentFundGroupMemberSchema)
  })
  .strict()

export const CommitmentFormOptionsSchema = z
  .object({
    // the investor entities of the account the options were asked for
    investors: z.array(CommitmentInvestorOptionSchema),
    // standalone funds (members of a consolidated fund are not offered)
    funds: z.array(CommitmentFundOptionSchema),
    fund_groups: z.array(CommitmentFundGroupOptionSchema),
    statuses: z.array(NumberOptionSchema),
    notice_supplements: z.array(NumberOptionSchema)
  })
  .strict()

export type FundCommitmentDetail = z.infer<typeof FundCommitmentDetailSchema>
export type FundGroupCommitmentAllocation = z.infer<typeof FundGroupCommitmentAllocationSchema>
export type FundGroupCommitmentDetail = z.infer<typeof FundGroupCommitmentDetailSchema>
export type CommitmentInvestorOption = z.infer<typeof CommitmentInvestorOptionSchema>
export type CommitmentFundOption = z.infer<typeof CommitmentFundOptionSchema>
export type CommitmentFundGroupMember = z.infer<typeof CommitmentFundGroupMemberSchema>
export type CommitmentFundGroupOption = z.infer<typeof CommitmentFundGroupOptionSchema>
export type CommitmentFormOptions = z.infer<typeof CommitmentFormOptionsSchema>

export const validateFundCommitmentDetail = (data: unknown): FundCommitmentDetail =>
  validateWithZodSchema(FundCommitmentDetailSchema, data, 'fund commitment detail')

export const validateFundGroupCommitmentDetail = (data: unknown): FundGroupCommitmentDetail =>
  validateWithZodSchema(FundGroupCommitmentDetailSchema, data, 'consolidated commitment detail')

export const validateCommitmentFormOptions = (data: unknown): CommitmentFormOptions =>
  validateWithZodSchema(CommitmentFormOptionsSchema, data, 'commitment form options')
